import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { User, Weight, Ruler, Calendar, AlertTriangle, Heart, X, Shield, Users, LogOut, Camera, TrendingUp, TrendingDown, Minus, Target, Activity, Flame, Droplets, Loader2, Gift, CheckCircle, Clock, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const Profile = () => {
  const { API, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    weight: '',
    height: '',
    age: '',
  });
  const [newAllergy, setNewAllergy] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [newPreference, setNewPreference] = useState('');
  const [freeAccessStatus, setFreeAccessStatus] = useState(null);
  const [vulnerableReason, setVulnerableReason] = useState('');
  const [vulnerableCategory, setVulnerableCategory] = useState('other');
  const [submittingFreeAccess, setSubmittingFreeAccess] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [healthMetrics, setHealthMetrics] = useState([]);
  const [healthStats, setHealthStats] = useState(null);
  
  // Weight Goal States
  const [weightGoalData, setWeightGoalData] = useState({
    target_weight: '',
    weight_goal: 'maintain',
    activity_level: 'moderate'
  });
  const [caloriePlan, setCaloriePlan] = useState(null);
  const [loadingCaloriePlan, setLoadingCaloriePlan] = useState(false);
  const [savingWeightGoal, setSavingWeightGoal] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchFreeAccessStatus();
    fetchHealthMetrics();
    fetchCaloriePlan();
  }, []);

  const fetchHealthMetrics = async () => {
    try {
      const response = await axios.get(`${API}/health/metrics`);
      const metrics = response.data.metrics || [];
      setHealthMetrics(metrics);
      
      // Calculate stats
      if (metrics.length > 0) {
        const latest = metrics[0];
        const previous = metrics[1];
        
        setHealthStats({
          glucose: {
            current: latest.glucose,
            previous: previous?.glucose,
            trend: latest.glucose && previous?.glucose 
              ? (latest.glucose - previous.glucose) 
              : 0
          },
          cholesterol: {
            current: latest.cholesterol,
            previous: previous?.cholesterol,
            trend: latest.cholesterol && previous?.cholesterol 
              ? (latest.cholesterol - previous.cholesterol) 
              : 0
          },
          weight: {
            current: profile?.weight,
            previous: null,
            trend: 0
          }
        });
      }
    } catch (error) {
      console.error('Error fetching health metrics:', error);
    }
  };

  const fetchFreeAccessStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/free-access-status`);
      setFreeAccessStatus(response.data);
    } catch (error) {
      console.error('Error fetching free access status:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/profile`);
      setProfile(response.data.profile);
      setFormData({
        weight: response.data.profile.weight || '',
        height: response.data.profile.height || '',
        age: response.data.profile.age || '',
      });
      // Load weight goal data
      setWeightGoalData({
        target_weight: response.data.profile.target_weight || '',
        weight_goal: response.data.profile.weight_goal || 'maintain',
        activity_level: response.data.profile.activity_level || 'moderate'
      });
      // Load saved profile image
      if (response.data.profile.profile_image) {
        setProfileImage(response.data.profile.profile_image);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Eroare la încărcarea profilului');
    }
  };

  const fetchCaloriePlan = async () => {
    setLoadingCaloriePlan(true);
    try {
      const response = await axios.get(`${API}/users/calorie-plan`);
      // API returns {profile_summary, calorie_plan} - we need calorie_plan
      setCaloriePlan(response.data.calorie_plan || response.data);
    } catch (error) {
      console.error('Error fetching calorie plan:', error);
      // Not showing error toast as this might fail if profile is incomplete
    } finally {
      setLoadingCaloriePlan(false);
    }
  };

  const handleSaveWeightGoal = async () => {
    setSavingWeightGoal(true);
    try {
      await axios.put(`${API}/users/weight-goal`, {
        target_weight: parseFloat(weightGoalData.target_weight) || null,
        weight_goal: weightGoalData.weight_goal,
        activity_level: weightGoalData.activity_level
      });
      toast.success('Obiectivele de greutate au fost salvate!');
      await fetchCaloriePlan(); // Refresh calorie plan after saving
      await fetchProfile();
    } catch (error) {
      console.error('Error saving weight goal:', error);
      toast.error(error.response?.data?.detail || 'Eroare la salvarea obiectivelor');
    } finally {
      setSavingWeightGoal(false);
    }
  };

  const handleUpdateBasicInfo = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/users/profile`, {
        weight: parseFloat(formData.weight) || null,
        height: parseFloat(formData.height) || null,
        age: parseInt(formData.age) || null,
      });
      toast.success('Profil actualizat cu succes!');
      fetchProfile();
    } catch (error) {
      toast.error('Eroare la actualizarea profilului');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (type, value) => {
    if (!value.trim()) return;

    try {
      const updates = {};
      const currentList = profile[type] || [];
      updates[type] = [...currentList, value.trim()];

      await axios.put(`${API}/users/profile`, updates);
      toast.success('Actualizat cu succes!');
      fetchProfile();

      if (type === 'allergies') setNewAllergy('');
      if (type === 'medical_conditions') setNewCondition('');
      if (type === 'dietary_preferences') setNewPreference('');
    } catch (error) {
      toast.error('Eroare la actualizare');
    }
  };

  const handleRemoveItem = async (type, value) => {
    try {
      const currentList = profile[type] || [];
      const updates = {};
      updates[type] = currentList.filter((item) => item !== value);

      await axios.put(`${API}/users/profile`, updates);
      toast.success('Eliminat cu succes!');
      fetchProfile();
    } catch (error) {
      toast.error('Eroare la eliminare');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Fișierul este prea mare. Maxim 2MB.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        setProfileImage(base64Image);
        
        try {
          // Save to backend
          await axios.put(`${API}/users/profile`, {
            profile_image: base64Image
          });
          toast.success('Poză de profil salvată cu succes!');
        } catch (error) {
          console.error('Error saving profile image:', error);
          toast.error('Eroare la salvarea pozei de profil');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const getTrendIcon = (trend) => {
    if (!trend || trend === 0) return <Minus className="w-4 h-4 text-gray-400" />;
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-red-500" />;
    return <TrendingDown className="w-4 h-4 text-green-500" />;
  };

  const getTrendColor = (trend) => {
    if (!trend || trend === 0) return 'text-gray-600';
    if (trend > 0) return 'text-red-600';
    return 'text-green-600';
  };

  const dietaryOptions = ['Vegetarian', 'Vegan', 'Fără gluten', 'Fără lactoză', 'Keto', 'Paleo', 'Mediterranean'];

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#F5F5F0]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <p className="text-[#4a5568]">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8 max-w-7xl" data-testid="profile-page">
        <div className="animate-fade-in">
          <div className="mb-6 md:mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
              Profilul Meu
            </h1>
            <p className="text-[#4a5568] text-base md:text-lg">
              Gestionează-ți datele personale și preferințele
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Profile Picture */}
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Camera className="w-6 h-6 mr-2" />
                  Poză de Profil
                </CardTitle>
                <CardDescription>Încarcă o poză pentru profilul tău</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center overflow-hidden border-4 border-[#1A4D2E]/20">
                      {profileImage ? (
                        <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-16 h-16 text-[#1A4D2E]/40" />
                      )}
                    </div>
                    <label
                      htmlFor="profile-image-upload"
                      className="absolute bottom-0 right-0 w-10 h-10 bg-[#1A4D2E] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#143d24] transition-all shadow-lg"
                    >
                      <Camera className="w-5 h-5 text-white" />
                    </label>
                    <input
                      id="profile-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="profile-image-upload"
                    />
                  </div>
                  <p className="text-sm text-[#4a5568] text-center">
                    Click pe cameră pentru a încărca o poză
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Basic Information */}
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <User className="w-6 h-6 mr-2" />
                  Informații de Bază
                </CardTitle>
                <CardDescription>Actualizează-ți datele personale</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateBasicInfo} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight" className="flex items-center space-x-2">
                      <Weight className="w-4 h-4" />
                      <span>Greutate (kg)</span>
                    </Label>
                    <Input
                      id="weight"
                      data-testid="weight-input"
                      type="number"
                      step="0.1"
                      placeholder="75.5"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="height" className="flex items-center space-x-2">
                      <Ruler className="w-4 h-4" />
                      <span>Înălțime (cm)</span>
                    </Label>
                    <Input
                      id="height"
                      data-testid="height-input"
                      type="number"
                      placeholder="175"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age" className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>Vârstă</span>
                    </Label>
                    <Input
                      id="age"
                      data-testid="age-input"
                      type="number"
                      placeholder="30"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="save-basic-info-button"
                    className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12"
                  >
                    {loading ? 'Se salvează...' : 'Salvează Modificările'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Weight Goals Section - NEW */}
            <Card className="border-none card-shadow lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Target className="w-6 h-6 mr-2" />
                  Obiective de Greutate
                </CardTitle>
                <CardDescription>Setează-ți obiectivele și primește un plan personalizat de calorii</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left: Form */}
                  <div className="space-y-6">
                    {/* Weight Goal Type */}
                    <div className="space-y-3">
                      <Label className="flex items-center space-x-2 text-sm font-medium">
                        <Target className="w-4 h-4" />
                        <span>Obiectivul tău</span>
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'lose', label: 'Slăbire', icon: TrendingDown, color: 'text-blue-600 bg-blue-50 border-blue-200' },
                          { value: 'maintain', label: 'Menținere', icon: Minus, color: 'text-green-600 bg-green-50 border-green-200' },
                          { value: 'gain', label: 'Creștere', icon: TrendingUp, color: 'text-orange-600 bg-orange-50 border-orange-200' }
                        ].map((goal) => (
                          <button
                            key={goal.value}
                            type="button"
                            onClick={() => setWeightGoalData({ ...weightGoalData, weight_goal: goal.value })}
                            data-testid={`weight-goal-${goal.value}`}
                            className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                              weightGoalData.weight_goal === goal.value
                                ? `${goal.color} border-current`
                                : 'bg-[#F5F5F0] border-transparent hover:border-[#1A4D2E]/20'
                            }`}
                          >
                            <goal.icon className="w-5 h-5" />
                            <span className="text-sm font-medium">{goal.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Target Weight */}
                    <div className="space-y-2">
                      <Label htmlFor="target_weight" className="flex items-center space-x-2">
                        <Weight className="w-4 h-4" />
                        <span>Greutate țintă (kg)</span>
                      </Label>
                      <Input
                        id="target_weight"
                        data-testid="target-weight-input"
                        type="number"
                        step="0.1"
                        placeholder={weightGoalData.weight_goal === 'lose' ? '65' : weightGoalData.weight_goal === 'gain' ? '80' : formData.weight || '70'}
                        value={weightGoalData.target_weight}
                        onChange={(e) => setWeightGoalData({ ...weightGoalData, target_weight: e.target.value })}
                        className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                      />
                      {formData.weight && weightGoalData.target_weight && (
                        <p className="text-xs text-[#4a5568]">
                          {parseFloat(weightGoalData.target_weight) < parseFloat(formData.weight) 
                            ? `Vei slăbi ${(parseFloat(formData.weight) - parseFloat(weightGoalData.target_weight)).toFixed(1)} kg`
                            : parseFloat(weightGoalData.target_weight) > parseFloat(formData.weight)
                            ? `Vei lua ${(parseFloat(weightGoalData.target_weight) - parseFloat(formData.weight)).toFixed(1)} kg`
                            : 'Menții greutatea actuală'}
                        </p>
                      )}
                    </div>

                    {/* Activity Level */}
                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <Activity className="w-4 h-4" />
                        <span>Nivel de activitate</span>
                      </Label>
                      <select
                        value={weightGoalData.activity_level}
                        onChange={(e) => setWeightGoalData({ ...weightGoalData, activity_level: e.target.value })}
                        data-testid="activity-level-select"
                        className="w-full h-12 px-4 bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg text-[#1A4D2E] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                      >
                        <option value="sedentary">Sedentar (puțină mișcare)</option>
                        <option value="light">Ușor activ (1-3 zile/săptămână)</option>
                        <option value="moderate">Moderat activ (3-5 zile/săptămână)</option>
                        <option value="active">Activ (6-7 zile/săptămână)</option>
                        <option value="very_active">Foarte activ (antrenamente intense)</option>
                      </select>
                    </div>

                    <Button
                      onClick={handleSaveWeightGoal}
                      disabled={savingWeightGoal || !formData.weight || !formData.height || !formData.age}
                      data-testid="save-weight-goal-button"
                      className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12"
                    >
                      {savingWeightGoal ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Se calculează...
                        </>
                      ) : (
                        <>
                          <Target className="w-4 h-4 mr-2" />
                          Calculează Planul de Calorii
                        </>
                      )}
                    </Button>

                    {(!formData.weight || !formData.height || !formData.age) && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
                        ⚠️ Completează mai întâi greutatea, înălțimea și vârsta în secțiunea &quot;Informații de Bază&quot;
                      </p>
                    )}
                  </div>

                  {/* Right: Calorie Plan Results */}
                  <div>
                    {loadingCaloriePlan ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
                      </div>
                    ) : caloriePlan && caloriePlan.target_calories ? (
                      <div className="space-y-4">
                        {/* Main Calorie Target */}
                        <div className="bg-gradient-to-br from-[#1A4D2E] to-[#2E7D4A] p-6 rounded-2xl text-white">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/80 text-sm">Ținta ta zilnică</span>
                            <Flame className="w-5 h-5 text-orange-300" />
                          </div>
                          <p className="text-4xl font-bold mb-1">{caloriePlan.target_calories}</p>
                          <p className="text-white/80 text-sm">calorii / zi</p>
                          <div className="mt-4 pt-4 border-t border-white/20">
                            <p className="text-sm">{caloriePlan.goal_description || ''}</p>
                            {caloriePlan.estimated_weeks && (
                              <p className="text-xs text-white/70 mt-1">
                                ~{caloriePlan.estimated_weeks} săptămâni pentru a ajunge la țintă
                              </p>
                            )}
                          </div>
                        </div>

                        {/* BMI & Stats */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-[#F5F5F0] p-4 rounded-xl">
                            <p className="text-xs text-[#4a5568] mb-1">IMC</p>
                            <p className="text-xl font-bold text-[#1A4D2E]">{caloriePlan.bmi || 'N/A'}</p>
                            <p className="text-xs text-[#4a5568] capitalize">{caloriePlan.bmi_category || ''}</p>
                          </div>
                          <div className="bg-[#F5F5F0] p-4 rounded-xl">
                            <p className="text-xs text-[#4a5568] mb-1">Metabolism bazal</p>
                            <p className="text-xl font-bold text-[#1A4D2E]">{caloriePlan.bmr || 'N/A'}</p>
                            <p className="text-xs text-[#4a5568]">kcal/zi</p>
                          </div>
                        </div>

                        {/* Macros */}
                        {caloriePlan.macros && (
                        <div className="bg-[#F5F5F0] p-4 rounded-xl">
                          <p className="text-sm font-medium text-[#1A4D2E] mb-3">Macronutrienți zilnici</p>
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                              <p className="text-lg font-bold text-[#1A4D2E]">{caloriePlan.macros?.protein_g || 0}g</p>
                              <p className="text-xs text-[#4a5568]">Proteine</p>
                              <div className="w-full h-1 bg-[#1A4D2E]/20 rounded mt-1">
                                <div className="h-1 bg-red-500 rounded" style={{ width: `${caloriePlan.macro_percentages?.protein || 0}%` }}></div>
                              </div>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-[#1A4D2E]">{caloriePlan.macros?.carbs_g || 0}g</p>
                              <p className="text-xs text-[#4a5568]">Carbohidrați</p>
                              <div className="w-full h-1 bg-[#1A4D2E]/20 rounded mt-1">
                                <div className="h-1 bg-yellow-500 rounded" style={{ width: `${caloriePlan.macro_percentages?.carbs || 0}%` }}></div>
                              </div>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-[#1A4D2E]">{caloriePlan.macros?.fat_g || 0}g</p>
                              <p className="text-xs text-[#4a5568]">Grăsimi</p>
                              <div className="w-full h-1 bg-[#1A4D2E]/20 rounded mt-1">
                                <div className="h-1 bg-blue-500 rounded" style={{ width: `${caloriePlan.macro_percentages?.fat || 0}%` }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        )}

                        {/* Water Intake */}
                        {caloriePlan.daily_water_ml && (
                        <div className="bg-blue-50 p-4 rounded-xl flex items-center gap-3">
                          <Droplets className="w-6 h-6 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium text-blue-800">Apă recomandată</p>
                            <p className="text-xs text-blue-600">{(caloriePlan.daily_water_ml / 1000).toFixed(1)}L pe zi</p>
                          </div>
                        </div>
                        )}

                        {/* Recommendations */}
                        {caloriePlan.recommendations && caloriePlan.recommendations.length > 0 && (
                          <div className="bg-green-50 p-4 rounded-xl">
                            <p className="text-sm font-medium text-green-800 mb-2">Recomandări personalizate</p>
                            <ul className="space-y-1">
                              {caloriePlan.recommendations.slice(0, 3).map((rec, i) => (
                                <li key={i} className="text-xs text-green-700">{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-[#F5F5F0] rounded-2xl">
                        <Target className="w-16 h-16 text-[#1A4D2E]/20 mb-4" />
                        <p className="text-[#4a5568] mb-2">Planul tău personalizat de calorii</p>
                        <p className="text-sm text-[#718096]">
                          Completează informațiile de bază și setează-ți obiectivul pentru a primi recomandări personalizate
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dietary Preferences */}
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Heart className="w-6 h-6 mr-2" />
                  Preferințe Alimentare
                </CardTitle>
                <CardDescription>Alege-ți tipul de alimentație</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {dietaryOptions.map((option) => {
                      const isSelected = profile.dietary_preferences?.includes(option);
                      return (
                        <Badge
                          key={option}
                          data-testid={`diet-option-${option.toLowerCase()}`}
                          onClick={() =>
                            isSelected
                              ? handleRemoveItem('dietary_preferences', option)
                              : handleAddItem('dietary_preferences', option)
                          }
                          className={`cursor-pointer px-4 py-2 text-sm rounded-full transition-all ${
                            isSelected
                              ? 'bg-[#1A4D2E] text-white hover:bg-[#143d24]'
                              : 'bg-[#F5F5F0] text-[#4a5568] hover:bg-[#E2E8F0]'
                          }`}
                        >
                          {option}
                        </Badge>
                      );
                    })}
                  </div>

                  <div className="flex space-x-2">
                    <Input
                      placeholder="Adăugă preferință personalizată..."
                      value={newPreference}
                      data-testid="add-preference-input"
                      onChange={(e) => setNewPreference(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddItem('dietary_preferences', newPreference)}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                    />
                    <Button
                      onClick={() => handleAddItem('dietary_preferences', newPreference)}
                      data-testid="add-preference-button"
                      className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                    >
                      Adăugă
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Allergies */}
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <AlertTriangle className="w-6 h-6 mr-2" />
                  Alergii
                </CardTitle>
                <CardDescription>Lista cu alergii alimentare</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {profile.allergies && profile.allergies.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.allergies.map((allergy) => (
                        <Badge
                          key={allergy}
                          data-testid={`allergy-${allergy}`}
                          className="bg-red-100 text-red-800 px-4 py-2 rounded-full flex items-center space-x-2"
                        >
                          <span>{allergy}</span>
                          <X
                            className="w-3 h-3 cursor-pointer"
                            onClick={() => handleRemoveItem('allergies', allergy)}
                          />
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#4a5568] text-sm">Nu ai adăugat alergii</p>
                  )}

                  <div className="flex space-x-2">
                    <Input
                      placeholder="Adăugă alergie..."
                      value={newAllergy}
                      data-testid="add-allergy-input"
                      onChange={(e) => setNewAllergy(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddItem('allergies', newAllergy)}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                    />
                    <Button
                      onClick={() => handleAddItem('allergies', newAllergy)}
                      data-testid="add-allergy-button"
                      className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                    >
                      Adăugă
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Medical Conditions */}
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Heart className="w-6 h-6 mr-2" />
                  Afecțiuni Medicale
                </CardTitle>
                <CardDescription>Condiții medicale relevante</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {profile.medical_conditions && profile.medical_conditions.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.medical_conditions.map((condition) => (
                        <Badge
                          key={condition}
                          data-testid={`condition-${condition}`}
                          className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full flex items-center space-x-2"
                        >
                          <span>{condition}</span>
                          <X
                            className="w-3 h-3 cursor-pointer"
                            onClick={() => handleRemoveItem('medical_conditions', condition)}
                          />
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#4a5568] text-sm">Nu ai adăugat afecțiuni medicale</p>
                  )}

                  <div className="flex space-x-2">
                    <Input
                      placeholder="Adăugă afecțiune..."
                      value={newCondition}
                      data-testid="add-condition-input"
                      onChange={(e) => setNewCondition(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddItem('medical_conditions', newCondition)}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                    />
                    <Button
                      onClick={() => handleAddItem('medical_conditions', newCondition)}
                      data-testid="add-condition-button"
                      className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                    >
                      Adăugă
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Health Evolution Monitoring */}
          <Card className="border-none card-shadow mt-8">
            <CardHeader>
              <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center justify-between">
                <span className="flex items-center">
                  <TrendingUp className="w-6 h-6 mr-2" />
                  Evoluție Sănătate
                </span>
                <a href="/health">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#1A4D2E]/20 text-[#1A4D2E] hover:bg-[#F5F5F0] rounded-full"
                  >
                    Vezi Detalii
                  </Button>
                </a>
              </CardTitle>
              <CardDescription>Monitorizează evoluția parametrilor de sănătate</CardDescription>
            </CardHeader>
            <CardContent>
              {healthMetrics.length > 0 ? (
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <div className="grid md:grid-cols-3 gap-4">
                    {healthStats?.glucose?.current && (
                      <div className="bg-[#F5F5F0] p-4 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[#4a5568]">Glicemie</span>
                          {getTrendIcon(healthStats.glucose.trend)}
                        </div>
                        <p className="text-2xl font-bold text-[#1A4D2E]">
                          {healthStats.glucose.current}
                          <span className="text-sm font-normal text-[#4a5568] ml-1">mg/dL</span>
                        </p>
                        {healthStats.glucose.trend !== 0 && (
                          <p className={`text-xs mt-1 ${getTrendColor(healthStats.glucose.trend)}`}>
                            {healthStats.glucose.trend > 0 ? '+' : ''}{healthStats.glucose.trend.toFixed(1)} față de ultima măsurătoare
                          </p>
                        )}
                      </div>
                    )}

                    {healthStats?.cholesterol?.current && (
                      <div className="bg-[#F5F5F0] p-4 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[#4a5568]">Colesterol</span>
                          {getTrendIcon(healthStats.cholesterol.trend)}
                        </div>
                        <p className="text-2xl font-bold text-[#1A4D2E]">
                          {healthStats.cholesterol.current}
                          <span className="text-sm font-normal text-[#4a5568] ml-1">mg/dL</span>
                        </p>
                        {healthStats.cholesterol.trend !== 0 && (
                          <p className={`text-xs mt-1 ${getTrendColor(healthStats.cholesterol.trend)}`}>
                            {healthStats.cholesterol.trend > 0 ? '+' : ''}{healthStats.cholesterol.trend.toFixed(1)} față de ultima măsurătoare
                          </p>
                        )}
                      </div>
                    )}

                    {profile?.weight && (
                      <div className="bg-[#F5F5F0] p-4 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[#4a5568]">Greutate</span>
                          <Weight className="w-4 h-4 text-[#1A4D2E]" />
                        </div>
                        <p className="text-2xl font-bold text-[#1A4D2E]">
                          {profile.weight}
                          <span className="text-sm font-normal text-[#4a5568] ml-1">kg</span>
                        </p>
                        <p className="text-xs text-[#4a5568] mt-1">
                          Înălțime: {profile.height || 'N/A'} cm
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Mini Chart */}
                  {healthMetrics.length > 1 && (
                    <div>
                      <h4 className="text-sm font-medium text-[#4a5568] mb-3">Evoluție Glicemie (ultimele 7 zile)</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={healthMetrics.slice(0, 7).reverse().map(m => ({
                          date: new Date(m.date).toLocaleDateString('ro-RO', { month: 'short', day: 'numeric' }),
                          value: m.glucose || 0
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="date" stroke="#4a5568" style={{ fontSize: '12px' }} />
                          <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
                          <Tooltip />
                          <Line 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#1A4D2E" 
                            strokeWidth={2} 
                            dot={{ fill: '#1A4D2E', r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Last Measurement */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Ultima măsurătoare:</strong> {new Date(healthMetrics[0].date).toLocaleDateString('ro-RO', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Heart className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                  <p className="text-[#4a5568] mb-4">Nu ai adăugat încă metrici de sănătate</p>
                  <a href="/health">
                    <Button
                      data-testid="add-health-metrics-button"
                      className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                    >
                      Adaugă Prima Măsurătoare
                    </Button>
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Security & Access */}
          <div className="grid lg:grid-cols-2 gap-8 mt-8">
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Shield className="w-6 h-6 mr-2" />
                  Securitate
                </CardTitle>
                <CardDescription>Protejează-ți contul</CardDescription>
              </CardHeader>
              <CardContent>
                <a href="/2fa">
                  <Button
                    className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                    data-testid="setup-2fa-link"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Configurează Autentificare 2FA
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Gift className="w-6 h-6 mr-2" />
                  Acces Gratuit
                </CardTitle>
                <CardDescription>Program de sprijin pentru utilizatori vulnerabili</CardDescription>
              </CardHeader>
              <CardContent>
                {freeAccessStatus?.has_free_access ? (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-green-800 font-bold text-lg">Acces Gratuit Activ</p>
                          <p className="text-green-600 text-sm">Nivel: {freeAccessStatus.free_access_tier?.toUpperCase() || 'PREMIUM'}</p>
                        </div>
                      </div>
                      
                      {freeAccessStatus.benefits?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-green-700 mb-2">Beneficiile tale:</p>
                          <ul className="space-y-1">
                            {freeAccessStatus.benefits.map((benefit, idx) => (
                              <li key={idx} className="text-sm text-green-600 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4" />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ) : freeAccessStatus?.request?.status === 'pending' ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-blue-800 font-bold">Cerere în Procesare</p>
                        <p className="text-blue-600 text-sm">Categoria: {freeAccessStatus.request.category_name}</p>
                      </div>
                    </div>
                    <p className="text-sm text-blue-700">
                      Cererea ta este analizată de echipa noastră. Vei primi un răspuns în 24-48 de ore.
                    </p>
                  </div>
                ) : freeAccessStatus?.request?.status === 'rejected' ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-4">
                    <p className="text-red-800 font-medium mb-2">Cererea a fost respinsă</p>
                    <p className="text-sm text-red-600">{freeAccessStatus.request.rejection_reason || 'Cererea nu îndeplinește criteriile.'}</p>
                    <p className="text-xs text-red-500 mt-2">Poți trimite o nouă cerere cu documentație suplimentară.</p>
                  </div>
                ) : null}
                
                {/* Form for new request */}
                {!freeAccessStatus?.has_free_access && freeAccessStatus?.request?.status !== 'pending' && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                      <div className="flex items-start gap-2">
                        <HelpCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Cine poate beneficia?</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Persoane cu venituri reduse, dizabilități, boli cronice, vârstnici, refugiați sau alte situații vulnerabile.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Category Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Categoria situației tale</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {freeAccessStatus?.categories && Object.entries(freeAccessStatus.categories).map(([key, cat]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setVulnerableCategory(key)}
                            className={`p-3 rounded-xl border text-left transition-all ${
                              vulnerableCategory === key
                                ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]'
                                : 'bg-[#F5F5F0] border-transparent hover:border-[#1A4D2E]/30'
                            }`}
                          >
                            <p className="font-medium text-sm">{cat.name}</p>
                            <p className={`text-xs mt-0.5 ${vulnerableCategory === key ? 'text-white/80' : 'text-[#4a5568]'}`}>
                              {cat.description.substring(0, 40)}...
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Reason Input */}
                    <div className="space-y-2">
                      <Label htmlFor="vulnerable-reason">Descrie situația ta</Label>
                      <textarea
                        id="vulnerable-reason"
                        placeholder="Te rugăm să descrii pe scurt situația ta și de ce ai nevoie de acces gratuit..."
                        value={vulnerableReason}
                        onChange={(e) => setVulnerableReason(e.target.value)}
                        data-testid="vulnerable-reason-input"
                        rows={3}
                        className="w-full px-4 py-3 bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-xl text-[#1A4D2E] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 resize-none"
                      />
                    </div>
                    
                    {/* Benefits Preview */}
                    {vulnerableCategory && freeAccessStatus?.categories?.[vulnerableCategory] && (
                      <div className="bg-[#F5F5F0] rounded-xl p-4">
                        <p className="text-sm font-medium text-[#1A4D2E] mb-2">Beneficii dacă cererea este aprobată:</p>
                        <ul className="space-y-1">
                          {freeAccessStatus.categories[vulnerableCategory].benefits.map((benefit, idx) => (
                            <li key={idx} className="text-sm text-[#4a5568] flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <Button
                      onClick={async () => {
                        if (!vulnerableReason.trim()) {
                          toast.error('Te rugăm să descrii situația ta');
                          return;
                        }
                        setSubmittingFreeAccess(true);
                        try {
                          const response = await axios.post(`${API}/users/request-free-access`, { 
                            reason: vulnerableReason,
                            category: vulnerableCategory
                          });
                          toast.success(response.data.message || 'Cererea a fost trimisă!');
                          setVulnerableReason('');
                          fetchFreeAccessStatus();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Eroare la trimiterea cererii');
                        } finally {
                          setSubmittingFreeAccess(false);
                        }
                      }}
                      disabled={!vulnerableReason.trim() || submittingFreeAccess}
                      data-testid="request-free-access-button"
                      className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12"
                    >
                      {submittingFreeAccess ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Se trimite...
                        </>
                      ) : (
                        <>
                          <Gift className="w-4 h-4 mr-2" />
                          Solicită Acces Gratuit
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Logout Section */}
          <Card className="border-none card-shadow mt-8 bg-red-50/50">
            <CardHeader>
              <CardTitle className="font-serif text-2xl text-red-700 flex items-center">
                <LogOut className="w-6 h-6 mr-2" />
                Deconectare
              </CardTitle>
              <CardDescription>Închide sesiunea curentă</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm text-[#4a5568] mb-2">
                    Ești autentificat în platformă
                  </p>
                  <p className="text-xs text-[#718096]">
                    Deconectarea te va trimite înapoi la pagina principală
                  </p>
                </div>
                <Button
                  onClick={() => {
                    if (window.confirm('Ești sigur că vrei să te deconectezi?')) {
                      logout();
                      window.location.href = '/';
                    }
                  }}
                  variant="destructive"
                  data-testid="logout-button-profile"
                  className="bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all w-full md:w-auto"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Deconectare
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;